import { supabase } from './supabaseClient';

export type DeckRow = {
  id: string;
  name: string;
  deck: any;
  updated_at?: string;
};

export async function getSessionUser() {
  const { data, error } = await supabase.auth.getUser();
  if (error) return null;
  return data.user ?? null;
}

export async function loginWithEmail(email: string, redirectTo: string) {
  // Legacy helper (magic-link style). Kept for backward compatibility.
  // Prefer requestEmailOtp + verifyEmailOtp for a PWA-friendly flow (no deep-link).
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: redirectTo }
  });
  if (error) throw error;
}

/**
 * Password auth (no emails) — ideal for playtests without SMTP.
 * Try to sign in; if the user doesn't exist yet, create it.
 */
export async function signInOrCreateWithPassword(email: string, password: string) {
  // 1) Try sign-in first
  const { data: inData, error: inErr } = await supabase.auth.signInWithPassword({
    email,
    password
  });
  if (!inErr) return inData;

  // 2) If sign-in fails, attempt sign-up (playtest mode: no email confirmations).
  const { data: upData, error: upErr } = await supabase.auth.signUp({
    email,
    password
  });

  if (!upErr) {
    // Depending on project settings, signUp may not return a session.
    // To make the UX deterministic, we sign-in right after a successful sign-up.
    const { data: postData, error: postErr } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (!postErr) return postData;
    // If for some reason sign-in still fails, bubble up the original sign-in error.
    throw postErr;
  }

  // If the user already exists, keep the original sign-in error (wrong password, etc.).
  const msg = (upErr as any)?.message || '';
  if (/already\s+registered|already\s+exists|user\s+already/i.test(msg)) {
    throw inErr;
  }
  throw upErr;
}


/**
 * PWA-friendly auth: send a one-time code by email (OTP).
 * Note: Supabase will send a code if Email OTP is enabled in your project.
 */
export async function requestEmailOtp(email: string) {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      shouldCreateUser: true
      // Important: do NOT set emailRedirectTo here, otherwise Supabase will prefer magic links.
    }
  });
  if (error) throw error;
}

export async function verifyEmailOtp(email: string, token: string) {
  const { data, error } = await supabase.auth.verifyOtp({
    email,
    token,
    type: 'email'
  });
  if (error) throw error;
  return data;
}

export async function logout() {
  await supabase.auth.signOut();
}

export async function saveDeck(name: string, deckObj: any) {
  const { data: userData } = await supabase.auth.getUser();
  const user = userData.user;
  if (!user) throw new Error('Not logged');

  const payload = {
    user_id: user.id,
    name,
    deck: deckObj
  };

  // Create a new row each time (simple). You can later add "update existing by id".
  const { data, error } = await supabase
    .from('decklists')
    .insert(payload)
    .select('id,name,updated_at')
    .single();

  if (error) throw error;
  return data as { id: string; name: string; updated_at?: string };
}

export async function listDecks(): Promise<Pick<DeckRow,'id'|'name'>[]> {
  const { data, error } = await supabase
    .from('decklists')
    .select('id,name,updated_at')
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return (data ?? []).map(d => ({ id: d.id, name: d.name }));
}

export async function loadDeck(id: string): Promise<DeckRow | null> {
  const { data, error } = await supabase
    .from('decklists')
    .select('id,name,deck,updated_at')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as DeckRow;
}

/**
 * Delete a saved deck by id.
 * (RLS should restrict deletes to the owner via user_id)
 */
export async function deleteDeck(id: string): Promise<void> {
  const { error } = await supabase
    .from('decklists')
    .delete()
    .eq('id', id);

  if (error) throw error;
}
